// What the study shows beside the board, move by move (decided 2026-11-01, the study redesigned): the evaluation as a
// bar on the board's edge, three rows under it — the move just played, the repertoire, the engine — and the moves of
// the practice as one line that leads back to any of them. The repertoire and the engine used to be two cards at the
// end of a folded "Analysis" panel, the same size as the statistics of the position; they are what is read every move,
// so they sit with the board and carry no box. What players and your own games did here stays in cards, under it.
//
// The engine's lines are the user's to see whenever "Lines" is on, their own move included: holding them back while
// the user thought left nothing to read, because the partner answers in a couple of seconds (2026-11-01).
//
// Each row keeps the switches it had as a card — the repertoire's hint, best move and list of moves, the engine's lines
// and the number hidden — because some readers play without them. The rules are domain/studyLive.ts.

import "./LivePanel.css";
import "../practice/Scoresheet.css";
import { useEffect, useRef, useState } from "preact/hooks";
import { store, game } from "../../state/index.ts";
import { useStore } from "../../state/useStore.ts";
import { K } from "../../state/keys.ts";
import type { PracticeMove } from "../../state/keys.ts";
import { VerdictStrip } from "../practice/VerdictStrip.tsx";
import { PracticeResult } from "../practice/PracticeResult.tsx";
import { numberedSan, practiceStop, repertoireRow, whiteShare } from "../../domain/studyLive.ts";
import type { RepertoireState } from "../../domain/studyLive.ts";
import { tallyView } from "../../domain/scoresheetView.ts";
import { DEFAULT_MAX_OWN_MOVES } from "../../domain/practiceRules.ts";
import { verdictGlyph, verdictLabel } from "../../domain/moveRating.ts";
import * as storage from "../../services/storage.ts";
import * as board from "../../legacy/board.js";
import * as practice from "../../legacy/practice.js";
import { evaluatePosition } from "../../legacy/engine.js";
import { formatScore, pvUciToSan } from "../../legacy/utils.js";

const study = K.boards("study");
const SHOWN_LINES = 3;

/** The switches as the reader left them on this device, before the engine is first asked anything. */
export function restoreLiveSettings() {
    store.batch((set) => {
        set("engine.evalHidden", storage.loadEngineEvalHidden());
        set("engine.showLines", storage.loadEngineLines());
    });
}

/** The score in pawns from White's side, and whether it is a mate. */
function useScore(): { pawns: number | null; mate: boolean; text: string | null } {
    const pawns = useStore(K.engine.latestScorePawns);
    const text = useStore(K.engine.latestScoreText);
    return { pawns, mate: !!text && text.replace("-", "").startsWith("M"), text };
}

// ---- The bar on the board's edge ----

export function EvalBar() {
    const enabled = useStore(K.engine.isEnabled);
    const hidden = useStore(K.engine.evalHidden);
    const { pawns, mate } = useScore();
    // The side the user plays is the side of the board nearest them
    const line = useStore(K.practice.line);
    const userSide = useStore(K.board.userSide);
    const orientation = line?.color || userSide || "white";
    if (!enabled || hidden) return null;
    const white = whiteShare(pawns, mate);
    return (
        <div class="eval-bar" data-orientation={orientation} aria-hidden="true">
            <span class="eval-bar__white" style={{ "--white": `${white}%` }} />
        </div>
    );
}

// ---- The rows ----

export function LivePanel() {
    return (
        <div class="live-panel">
            <PracticeResult />
            <StopNotice />
            <dl class="live">
                <VerdictStrip />
                <RepertoireRow />
                <EngineRow />
            </dl>
        </div>
    );
}

/** The practice waiting for the user: why it stopped, and the two ways on, where nobody can miss them. */
function StopNotice() {
    const status = useStore(K.practice.status);
    const offbook = useStore(K.practice.offbook);
    const notice = useStore(K.practice.notice);
    if (status !== "offbook") return null;
    const stop = practiceStop(offbook?.rarity, notice);
    const takeBack = (
        <button type="button" class={`btn ${stop.primary === "takeBack" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => practice.takeBack()}>Take back my move</button>
    );
    const finish = (
        <button type="button" class={`btn ${stop.primary === "finish" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => practice.finishOffBook()}>End the practice here</button>
    );
    return (
        <section class="practice-stop" role="status">
            <h3 class="practice-stop__title">{stop.title}</h3>
            <p class="practice-stop__text">{stop.text}</p>
            <div class="toolbar">
                {stop.primary === "takeBack" ? [takeBack, finish] : [finish, takeBack]}
            </div>
        </section>
    );
}

function RepertoireRow() {
    const loggedIn = !!useStore(K.user.currentUser);
    const state = useStore(K.repertoire.status) as RepertoireState | null;
    const moves = (useStore(K.repertoire.latestMoves) || []) as string[];
    const hintMode = useStore(K.repertoire.hintMode);
    const history = useStore(study.history) || [];
    const baseFen = useStore(study.baseFen);
    const status = useStore(K.practice.status);
    const [open, setOpen] = useState(storage.loadRepertoireMovesOpen());

    // A hint about moves that are not there any more points at nothing
    useEffect(() => {
        if (!moves.length && hintMode) {
            store.set("repertoire.hintMode", null);
            board.updateBookOptions();
        }
    }, [moves.length]);

    const [, baseTurn, , , , baseNumber] = (baseFen || "").split(" ");
    const numbered = (ply: number, san: string) =>
        numberedSan(ply, san, baseTurn === "b" ? "b" : "w", Number(baseNumber) || 1);
    const row = repertoireRow({ loggedIn, state, moves: moves.length, numbered });
    // Taking back undoes the user's last move: offered only when that is the move that left, and it was played in
    // this practice (a move the line dealt cannot be taken back). A practice move's ply counts from 0.
    const deviation = state?.deviation;
    const practiceMoves = useStore(K.practice.moves) || [];
    const playedHere = !!deviation && practiceMoves.some((m) => m?.actor === "user" && m.ply === deviation.ply - 1);
    const canTakeBack = row.takeBack && playedHere && deviation!.ply >= history.length - 1
        && (status === "playing" || status === "offbook");

    const toggleHint = (mode: "hint" | "move") => {
        store.set("repertoire.hintMode", hintMode === mode ? null : mode);
        board.updateBookOptions();
    };
    const toggleList = () => {
        setOpen(!open);
        storage.saveRepertoireMovesOpen(!open);
    };

    return (
        <div class="live-row" data-tone={row.tone}>
            <dt class="live-row__label">Repertoire</dt>
            <dd class="live-row__value">
                <div class="live-row__line">
                    <span class="live-row__text">
                        {row.mark && <span class="live-row__mark">{row.mark}</span>}
                        {row.text}
                        {row.expected && <span class="live-row__aside"> · yours: <b class="mono">{row.expected}</b></span>}
                    </span>
                    {!loggedIn && <a class="card__link" href="/login">Log in</a>}
                    {canTakeBack && (
                        <button type="button" class="btn btn-ghost btn-sm" onClick={() => practice.takeBack()}>Take back</button>
                    )}
                    {row.tools && (
                        <span class="live-row__tools">
                            <button type="button" class="btn btn-ghost btn-sm" aria-pressed={hintMode === "hint"}
                                    title="Circle the piece to move" onClick={() => toggleHint("hint")}>Hint</button>
                            <button type="button" class="btn btn-ghost btn-sm" aria-pressed={hintMode === "move"}
                                    title="Show the move on the board" onClick={() => toggleHint("move")}>Best move</button>
                            <button type="button" class="btn btn-ghost btn-sm" aria-pressed={open}
                                    title="List the repertoire's moves from here" onClick={toggleList}>Moves</button>
                        </span>
                    )}
                </div>
                {row.tools && open && (
                    <ol class="live-lines">
                        {moves.map((pv) => (
                            <li key={pv}>
                                <b class="mono">{pvUciToSan(pv.split(/\s+/)[0], game.fen(), 1)}</b>{" "}
                                <span class="live-lines__moves">{pvUciToSan(pv, game.fen(), 6)}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </dd>
        </div>
    );
}

interface PvInfo {
    pv?: string;
    depth?: number;
}

function EngineRow() {
    const enabled = useStore(K.engine.isEnabled);
    const error = useStore(K.engine.error);
    const depth = useStore(K.engine.latestDepth);
    const hidden = useStore(K.engine.evalHidden);
    const showLines = useStore(K.engine.showLines);
    const pvLines = (useStore(K.engine.pvLines) || {}) as Record<number, PvInfo>;
    const fen = useStore(study.fen) || game.fen();
    const { pawns, text } = useScore();

    // The arrows on the board follow the same rule as the lines written here, and move with them
    const frame = useRef(0);
    useEffect(() => {
        if (!showLines) return;
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(() => board.updateBookOptions());
    }, [pvLines, showLines]);

    if (!enabled || error) {
        return (
            <div class="live-row" data-tone="muted">
                <dt class="live-row__label">Engine</dt>
                <dd class="live-row__value">
                    <div class="live-row__line">
                        <span class="live-row__text">{error ? `Engine error: ${error}` : "Engine off"}</span>
                        <a class="card__link" href="/settings">{error ? "Settings" : "Turn it on"}</a>
                    </div>
                </dd>
            </div>
        );
    }

    const toggleLines = () => {
        store.set("engine.showLines", !showLines);
        storage.saveEngineLines(!showLines);
        board.updateBookOptions();
        void evaluatePosition();
    };
    const toggleEval = () => {
        store.set("engine.evalHidden", !hidden);
        storage.saveEngineEvalHidden(!hidden);
    };

    const score = pawns === null ? "…" : text && text.replace("-", "").startsWith("M") ? text
        : `${pawns > 0 ? "+" : pawns < 0 ? "−" : ""}${Math.abs(pawns).toFixed(1)}`;
    const lines = Object.keys(pvLines).map(Number).sort((a, b) => a - b).slice(0, SHOWN_LINES)
        .map((n) => pvLines[n]).filter((info): info is PvInfo & { pv: string } => !!info?.pv);

    return (
        <div class="live-row">
            <dt class="live-row__label">Engine</dt>
            <dd class="live-row__value">
                <div class="live-row__line">
                    <span class="live-row__text">
                        {hidden ? <span class="live-row__aside">Evaluation hidden</span>
                            : <b class="live-score" data-sign={pawns === null ? 0 : Math.sign(pawns)}>{score}</b>}
                        {depth ? <span class="live-row__aside"> · depth {depth}</span> : null}
                    </span>
                    <span class="live-row__tools">
                        <button type="button" class="btn btn-ghost btn-sm" aria-pressed={showLines}
                                title="The engine's best lines, written here and drawn on the board" onClick={toggleLines}>Lines</button>
                        <button type="button" class="btn btn-ghost btn-sm" aria-pressed={!hidden}
                                title="The evaluation, as a number and as the bar" onClick={toggleEval}>{hidden ? "Show eval" : "Hide eval"}</button>
                    </span>
                </div>
                {showLines && lines.length > 0 && (
                        <ol class="live-lines live-lines--engine">
                            {lines.map((info, index) => (
                                <li key={index}>
                                    <b class="mono">{hidden ? "" : formatScore(info, fen)}</b>{" "}
                                    <span class="live-lines__moves">{pvUciToSan(info.pv, fen, 8)}</span>
                                </li>
                            ))}
                        </ol>
                    )}
            </dd>
        </div>
    );
}

// ---- The moves of the practice, as one line ----

const BAD = new Set(["mistake", "blunder"]);

export function MoveTape() {
    const ownMoves = useStore(K.practice.ownMoves) || 0;
    const moves = (useStore(K.practice.moves) || []).filter((m): m is PracticeMove => !!m);
    const status = useStore(K.practice.status);
    const tally = tallyView({
        status,
        ownMoves,
        combo: useStore(K.practice.combo) || 0,
        accuracy: useStore(K.practice.accuracy),
        xp: useStore(K.practice.xp) || 0,
        isRetry: useStore(K.practice.isRetry),
    });
    const list = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (list.current) list.current.scrollLeft = list.current.scrollWidth;
    }, [moves.length]);

    const finished = status === "finished";
    return (
        <div class="move-tape">
            {tally.moves && (
                <div class="move-tape__progress">
                    {/* When the practice ends, said and shown: after the user's tenth move, or earlier when the book ends */}
                    <span class="move-tape__steps" aria-hidden="true">
                        {Array.from({ length: Math.max(DEFAULT_MAX_OWN_MOVES, ownMoves) }, (_, i) => (
                            <i key={i} data-done={i < ownMoves || undefined} data-extra={i >= DEFAULT_MAX_OWN_MOVES || undefined} />
                        ))}
                    </span>
                    <p class="move-tape__tally">
                        {finished ? "Practice over"
                            : ownMoves >= DEFAULT_MAX_OWN_MOVES
                                ? `${tally.moves} · it goes on while you play your repertoire`
                                : `${tally.moves} · it ends after your ${DEFAULT_MAX_OWN_MOVES}th move, or later while you play your repertoire`}
                        {tally.showCombo && !finished ? ` · ${tally.combo}` : ""}
                    </p>
                </div>
            )}
            {moves.length > 0 && (
                <div class="move-tape__moves" ref={list} aria-label="Moves of this practice">
                    {moves.map((move, index) => {
                        const [, turn, , , , fullmove] = move.fenBefore.split(" ");
                        const number = turn === "w" ? `${fullmove}.` : index === 0 ? `${fullmove}…` : "";
                        // The moves the line deals have no verdict: they were not played
                        const code = !move.verdict ? "" : move.verdict.status === "rated" ? move.verdict.code ?? "" : move.verdict.status;
                        const mine = move.actor === "user";
                        // A move of yours, once it is over, is where to play from again; anything else is to look at
                        const retry = finished && mine;
                        return (
                            <button key={`${move.ply}-${move.san}`} type="button"
                                    class={"move-tape__move" + (mine ? " move-tape__move--mine" : "")
                                        + (mine && BAD.has(code) ? " move-tape__move--bad" : "")}
                                    data-verdict={mine ? code : undefined}
                                    title={retry ? "Play again from here" : mine && code ? verdictLabel(code as never) : undefined}
                                    onClick={() => (retry ? practice.retryFrom(move.ply) : board.jumpToPly(move.ply))}>
                                {number && <span class="move-tape__number">{number}</span>}
                                {move.san}
                                {mine && move.verdict?.status === "rated" && (
                                    <span class="move-tape__glyph">{verdictGlyph(move.verdict.code ?? null)}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
